import { RefObject, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useGlobalStore } from '../../../../../store/global-store/global-store';
import { useThroughputTabStore } from '../../../../../store/strategy-store/strategy-store';
import { setThroughputMetric } from '../../../../../store/strategy-store/strategy-store.actions';
import { FloraButton } from '../../../../flora/flora-button';
import { useCurrentStrategyTab } from '../../../strategy.hooks';
import { Metric, StrategyTab } from '../../../strategy.type';
import { MainSectionControls } from '../../components/main-section-controls/main-section-controls';
import { hasArrayData } from '../../initiative-performance.helpers';
import { ThroughputMode } from '../../initiative-performance.type';
import { useChartData as useContributingEpicsChartData } from './contributing-epics-chart/contributing-epics-chart.hooks';
import { useChartData as useContributingInitiativesChartData } from './contributing-initiatives-chart/contributing-initiatives-chart.hooks';
import { useChartData as useContributingOutcomesChartData } from './contributing-outcomes-chart/contributing-outcomes-chart.hooks';
import { useChartData as useContributingTeamsChartData } from './contributing-teams-chart/contributing-teams-chart.hooks';
import {
  formatContributingEpicsDataForFlora,
  formatInitiativeProgressDataForFlora,
} from './initiative-progress-chart/initiative-progress-chart.helpers';
import { useChartData as useInitiativeProgressChartData } from './initiative-progress-chart/initiative-progress-chart.hooks';

type Props = {
  chartRef: RefObject<HTMLDivElement>;
};

export const MainSectionHeader = ({ chartRef }: Props) => {
  const i18n = useTranslation();
  const useAI = useGlobalStore((state) => state.useAI);
  const mode = useThroughputTabStore((state) => state.mode);
  const metric = useThroughputTabStore((state) => state.metric);
  const selectedEpics = useThroughputTabStore((state) => state.selectedEpics);
  const selectedTeams = useThroughputTabStore((state) => state.selectedTeams);
  const selectedInitiatives = useThroughputTabStore((state) => state.selectedInitiatives);
  const selectedOutcomes = useThroughputTabStore((state) => state.selectedOutcomes);
  const currentStrategyTab = useCurrentStrategyTab();

  const initiativeProgressChartData = useInitiativeProgressChartData(metric);
  const contributingEpicsChartData = useContributingEpicsChartData();
  const contributingTeamsChartData = useContributingTeamsChartData();
  const contributingInitiativesChartData = useContributingInitiativesChartData();
  const contributingOutcomesChartData = useContributingOutcomesChartData();
  const hasData = useMemo(() => {
    switch (mode) {
      case ThroughputMode.InitiativeProgress:
        return hasArrayData(initiativeProgressChartData);
      case ThroughputMode.ContributingEpics:
        return hasArrayData(contributingEpicsChartData) && selectedEpics.length > 0;
      case ThroughputMode.ContributingTeams:
        return hasArrayData(contributingTeamsChartData) && selectedTeams.length > 0;
      case ThroughputMode.ContributingInitiatives:
        return hasArrayData(contributingInitiativesChartData) && selectedInitiatives.length > 0;
      case ThroughputMode.ContributingOutcomes:
        return hasArrayData(contributingOutcomesChartData) && selectedOutcomes.length > 0;
      default:
        return false;
    }
  }, [
    initiativeProgressChartData,
    contributingEpicsChartData,
    contributingTeamsChartData,
    contributingInitiativesChartData,
    contributingOutcomesChartData,
    mode,
    selectedEpics,
    selectedTeams,
    selectedInitiatives,
    selectedOutcomes,
  ]);

  const title = i18n.t('strategy.initiative_performance.throughput');

  const { floraPrompt, floraData } = useMemo(() => {
    switch (mode) {
      case ThroughputMode.InitiativeProgress:
        return {
          floraPrompt: i18n.t('flora.strategy.throughput_tab.initiative_progress_chart_prompt'),
          floraData: formatInitiativeProgressDataForFlora(initiativeProgressChartData, metric, i18n.t),
        };
      case ThroughputMode.ContributingEpics:
        return {
          floraPrompt: i18n.t('flora.strategy.throughput_tab.contributing_epics_chart_prompt'),
          floraData: formatContributingEpicsDataForFlora(contributingEpicsChartData, selectedEpics),
        };
      case ThroughputMode.ContributingTeams:
        return {
          floraPrompt: i18n.t('flora.strategy.throughput_tab.contributing_teams_chart_prompt'),
          floraData: contributingTeamsChartData,
        };
      default:
        return { floraPrompt: '', floraData: {} };
    }
  }, [
    mode,
    metric,
    selectedEpics,
    initiativeProgressChartData,
    contributingEpicsChartData,
    contributingTeamsChartData,
    i18n.t,
  ]);

  return (
    <MainSectionControls
      title={title}
      chartRef={chartRef}
      metric={metric}
      setMetric={setThroughputMetric}
      disableControls={!hasData}
      floraButton={
        // onyl show for initiatives for now. need to support goals & outcomes later
        useAI && currentStrategyTab === StrategyTab.Initiatives ? (
          <FloraButton
            externalData={[
              {
                unitType:
                  metric === Metric.Tasks
                    ? i18n.t('common.work_units').toLowerCase()
                    : i18n.t('common.estimation').toLowerCase(),
              },
              floraData,
            ]}
            prompt={floraPrompt}
            tooltipText={i18n.t('flora.strategy.flora_button_tooltip')}
          />
        ) : undefined
      }
    />
  );
};
